import React, { useEffect, useState } from 'react';
import { Grid, Table, TableBody, TableHead, Typography } from '@mui/material';
import TextField from '@mui/material/TextField';
import { DataRow, InputMappedDataRow } from '../../types/common';
import ConfirmationDialog from '../UI/ConfirmationDialog';
import ErrorPopup from '../ErrorPopup/ErrorPopup';
import { useAppDispatch, useAppSelector } from '../../state/hooks';
import { RootState } from '../../state/store';
import {
  deleteInputMappingRow,
  editInputMappingRow,
} from '../../pages/ScenarioCreation/components/ReviewTable/reviewTableSlice';
import {
  StyledTableContainer,
  StyledTableHeadRow,
  StyledTableCellHead,
  StyledTableRow,
  StyledTableCellBody,
  StyledTypography,
} from '../AudioRecorder/ResultTable.Styled';
import CustomButton from '../UI/CustomButton/CustomButton';
import { hideLoader, showLoader } from '../../features/ui/loaderSlice';
import { saveScenarioDraft } from '../../features/scenarioCreation/scenarioCreationSlice';
import FilterListIcon from '@mui/icons-material/FilterList';
import { TableFilterDropdown } from './TableFilterDropdown';
import { StyledCheckbox } from '../../utils/commonStyles.Styled';
import { ColumnContainer } from '../../pages/ScenarioCreation/components/ReviewTable/ReviewTable.Styled';
import { setNoOfInterventions } from '../FormComponent/formSlice';

const inputMappedTablecolumns = [
  { id: 'State', label: 'States' },
  { id: 'Category', label: 'Category' },
  { id: 'Bottler', label: 'Bottler' },
  { id: 'Brand', label: 'Brand' },
  { id: 'Pack_Segment', label: 'Pack Segment' },
  { id: 'Pack_Size', label: 'Pack Size' },
  { id: 'Current_Price', label: 'Current Price' },
  { id: 'New_Price', label: 'New Price' },
  { id: 'Current_Distribution', label: 'Current Dist.' },
  { id: 'New_Distribution', label: 'New Dist.' },
  { id: 'Pack_Size_Replace', label: 'Replaced Size' },
  { id: 'Action', label: 'Actions' },
  { id: 'actions', label: 'Select to Delete' },
];

const filterableColumns = ['State', 'Category', 'Bottler', 'Brand', 'Pack_Segment', 'Action', 'Pack_Size'];

const staticInputResultData: InputMappedDataRow[] = [
  {
    State: '--',
    Category: '--',
    Bottler: '--',
    Brand: '--',
    Pack_Segment: '--',
    Pack_Size: '--',
    New_Price: '--',
    New_Distribution: '--',
    Current_Price: '--',
    Current_Distribution: '--',
    Action: '--',
  },
];

interface ResultTableProps {
  mappings: Record<string, any>[];
  error: string | null;
}

export const ReviewResultTable: React.FC<ResultTableProps> = ({ mappings, error }) => {
  const dispatch = useAppDispatch();

  const reviewTableMappings = useAppSelector((state: RootState) => state.reviewTable.mappings);
  const [dataRows, setDataRows] = useState<any[]>(mappings.length === 0 ? staticInputResultData : mappings);
  const [editCell, setEditCell] = useState<{
    rowIndex: number;
    columnId: string;
  } | null>(null);
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [errorPopupOpen, setErrorPopupOpen] = useState<boolean>(false);
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const filtersState = useAppSelector((state) => state.filters);
  const { selectedSegment, selectedAvailablePack, distributionSelected, priceInput } = useAppSelector(
    (state: RootState) => state.form,
  );
  const { selectedBrands } = useAppSelector((state: RootState) => state.brand);
  const finalMappings = useAppSelector((state: RootState) => state.reviewTable.mappings);
  const { current_tab } = useAppSelector((state: RootState) => state.inputKpi);
  const scenarioName = useAppSelector((state: RootState) => state.scenarioCreation.scenarioName);
  const [filterAnchorEl, setFilterAnchorEl] = useState<null | HTMLElement>(null);
  const [currentFilterColumn, setCurrentFilterColumn] = useState<string | null>(null);
  const [filters, setFilters] = useState<Record<string, string[]>>({});

  useEffect(() => {
    if (error) {
      setErrorPopupOpen(true);
    }
  }, [error]);

  useEffect(() => {
    if (mappings.length > 0) {
      setDataRows(mappings);

      const initialFilters: Record<string, string[]> = {};
      inputMappedTablecolumns.forEach((column) => {
        if (column.id !== 'actions') {
          const uniqueValues = Array.from(new Set(mappings.map((row) => row[column.id])));
          initialFilters[column.id] = uniqueValues;
        }
      });
      setFilters(initialFilters);
    } else {
      setDataRows(staticInputResultData);

      const initialFilters: Record<string, string[]> = {};
      inputMappedTablecolumns.forEach((column) => {
        if (column.id !== 'actions') {
          const uniqueValues = Array.from(new Set(staticInputResultData.map((row) => row[column.id])));
          initialFilters[column.id] = uniqueValues;
        }
      });
      setFilters(initialFilters);
    }
  }, [reviewTableMappings]);

  const handleEditCell = (rowIndex: number, columnId: string) => {
    setEditCell({ rowIndex, columnId });
  };

  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    rowIndex: number,
    columnId: string,
  ) => {
    const newValue = event.target.value;
    setDataRows((prevRows) => {
      const newRows = [...prevRows];
      newRows[rowIndex] = { ...newRows[rowIndex], [columnId]: newValue };
      return newRows;
    });
  };

  const handleBlur = (rowIndex: number) => {
    dispatch(editInputMappingRow({ rowIndex, updatedRow: dataRows[rowIndex] }));
    setEditCell(null);
  };

  const isRowRed = (row: any) => {
    return row.valid === false;
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleCloseErrorPopup = () => {
    setErrorPopupOpen(false);
  };

  const formatNumber = (value: any): string => {
    if (typeof value === 'number') {
      return value.toFixed(2);
    }
    return value;
  };

  const getPayload = () => {
    return {
      bottler: filtersState.enabledKeys.bottler,
      state: filtersState.enabledKeys.state,
      category: filtersState.enabledKeys.category,
      pack_segment: [selectedSegment],
      pack_size: selectedAvailablePack ? [selectedAvailablePack] : [],
      brand: selectedBrands,
      price: priceInput,
      distribution: distributionSelected,
      intervention_type: current_tab === 'Pricing & Distribution' ? 'Pricing' : current_tab,
      scenario_name: scenarioName,
    };
  };

  const saveToDraft = async () => {
    await dispatch(showLoader());
    const payload = getPayload();
    await dispatch(saveScenarioDraft({ ...payload, mappings: finalMappings }));
    await dispatch(hideLoader());
  };

  const handleFilterIconClick = (columnId: string, event: React.MouseEvent<HTMLElement>) => {
    setFilterAnchorEl(event.currentTarget);
    setCurrentFilterColumn(columnId);
  };

  const handleFilterChange = (columnId: string, value: string, isChecked: boolean) => {
    setFilters((prevFilters) => {
      const newFilters = { ...prevFilters };
      if (isChecked) {
        newFilters[columnId] = [...(newFilters[columnId] || []), value];
      } else {
        newFilters[columnId] = (newFilters[columnId] || []).filter((v) => v !== value);
      }
      return newFilters;
    });
  };

  const handleCloseFilterDropdown = () => {
    setFilterAnchorEl(null);
    setCurrentFilterColumn(null);
  };

  const getFilteredRows = () => {
    return dataRows
      .map((row, index) => ({ ...row, originalIndex: index }))
      .filter((row) => {
        return Object.entries(filters).every(([columnId, selectedValues]) => {
          if (selectedValues.length === 0) return true;
          return selectedValues.includes(row[columnId]);
        });
      });
  };

  const filteredRows = getFilteredRows();

  const handleDeleteClick = (rowIndex: number) => {
    setSelectedRows((prevSelected) => {
      if (prevSelected.includes(rowIndex)) {
        return prevSelected.filter((index) => index !== rowIndex);
      }
      return [...prevSelected, rowIndex];
    });
  };

  const deleteSelectedRows = () => {
    setOpenDialog(true);
  };

  const handleConfirmDelete = async () => {
    await dispatch(showLoader());

    const indicesToDelete = new Set(selectedRows.map((index) => filteredRows[index].originalIndex));
    await dispatch(deleteInputMappingRow(indicesToDelete));
    const newMappings = finalMappings.filter((_, index) => !indicesToDelete.has(index));
    await dispatch(setNoOfInterventions(newMappings.length));
    setSelectedRows([]);
    setOpenDialog(false);

    const payload = getPayload();
    await dispatch(saveScenarioDraft({ ...payload, mappings: newMappings }));
    await dispatch(hideLoader());
  };

  const handleSelectAllToDelete = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelectedRows(filteredRows.map((_, index) => index));
    } else {
      setSelectedRows([]);
    }
  };

  const isSelectAllChecked =
    selectedRows.length > 0 && filteredRows.length > 0 && selectedRows.length === filteredRows.length;

  return (
    <>
      <Grid
        style={{
          position: 'relative',
          marginBottom: '10px',
          display: 'flex',
          justifyContent: 'flex-end',
        }}
      >
        <CustomButton
          onClick={deleteSelectedRows}
          disabled={selectedRows.length === 0}
          style={{
            backgroundColor: selectedRows.length === 0 ? '#d3d3d3' : '#E81C0E',
            color: selectedRows.length === 0 ? '#7d7d7d' : '#fff',
            cursor: selectedRows.length === 0 ? 'not-allowed' : 'pointer',
            marginRight: '10px',
          }}
        >
          Delete
        </CustomButton>
        <CustomButton style={{ background: 'white', color: 'black' }} onClick={saveToDraft}>
          Save
        </CustomButton>
      </Grid>
      <StyledTableContainer
        style={{
          maxHeight: 480,
        }}
      >
        <Table stickyHeader>
          <TableHead>
            <StyledTableHeadRow>
              {inputMappedTablecolumns.map((column) => (
                <StyledTableCellHead
                  align="center"
                  key={column.id}
                  style={{
                    borderBottom: '1px solid #ddd',
                    borderRight: '1px solid #ddd',
                    backgroundColor: '#f7f7f7',
                    zIndex: 2,
                  }}
                >
                  <div
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}
                  >
                    <ColumnContainer>
                      <StyledTypography sx={{ fontWeight: '700' }}>{column.label}</StyledTypography>
                      {filterableColumns.includes(column.id) && (
                        <FilterListIcon
                          style={{ cursor: 'pointer', marginLeft: '5px', fontSize: '12px' }}
                          onClick={(e) => handleFilterIconClick(column.id, e)}
                        />
                      )}
                    </ColumnContainer>
                    {column.id === 'actions' && (
                      <ColumnContainer>
                        <StyledCheckbox
                          checked={isSelectAllChecked}
                          indeterminate={selectedRows.length > 0 && selectedRows.length < filteredRows.length}
                          onChange={handleSelectAllToDelete}
                          color="primary"
                        />
                        <Typography style={{ fontSize: '12px', marginRight: '10px' }}>Select All</Typography>
                      </ColumnContainer>
                    )}
                  </div>
                </StyledTableCellHead>
              ))}
            </StyledTableHeadRow>
          </TableHead>
          <TableBody>
            {filteredRows.map((row: DataRow, rowIndex: number) => (
              <StyledTableRow
                key={rowIndex}
                style={{
                  backgroundColor: selectedRows.includes(rowIndex) ? '#fef6f6' : isRowRed(row) ? '#ffebeb' : 'inherit',
                  color: isRowRed(row) ? '#E81C0E' : 'inherit',
                }}
              >
                {inputMappedTablecolumns.map((column) => (
                  <StyledTableCellBody
                    align="center"
                    padding="none"
                    key={column.id}
                    style={{
                      borderBottom: '1px solid #ddd',
                      borderRight: '1px solid #ddd',
                    }}
                  >
                    <StyledTypography
                      style={{
                        color: isRowRed(row) ? '#E81C0E' : 'inherit',
                        paddingLeft: '15px',
                        paddingRight: '15px',
                        fontSize: '10px',
                      }}
                    >
                      {column.id !== 'actions' ? (
                        editCell?.rowIndex === rowIndex && editCell.columnId === column.id ? (
                          <TextField
                            key={`${rowIndex}-${column.id}`}
                            value={row[column.id as keyof DataRow] || ''}
                            onChange={(e) => handleChange(e, rowIndex, column.id)}
                            onBlur={() => handleBlur(rowIndex)}
                            variant="standard"
                            fullWidth
                            autoFocus
                            style={{
                              backgroundColor: isRowRed(row) ? '#ffebeb' : 'inherit',
                              color: isRowRed(row) ? '#E81C0E' : 'inherit',
                              textAlign: 'center',
                            }}
                            InputProps={{
                              style: {
                                textAlign: 'center',
                                fontSize: '14px',
                                color: isRowRed(row) ? '#E81C0E' : 'inherit',
                              },
                            }}
                          />
                        ) : (
                          <button
                            // onClick={() => handleEditCell(rowIndex, column.id)}
                            style={{
                              cursor: 'pointer',
                              background: 'none',
                              border: 'none',
                              padding: 0,
                              font: 'inherit',
                            }}
                          >
                            {formatNumber(row[column.id as keyof DataRow]) || '--'}
                          </button>
                        )
                      ) : (
                        <div>
                          <StyledCheckbox
                            checked={selectedRows.includes(rowIndex)}
                            onChange={() => handleDeleteClick(rowIndex)}
                            color="primary"
                          />
                        </div>
                      )}
                    </StyledTypography>
                  </StyledTableCellBody>
                ))}
              </StyledTableRow>
            ))}
          </TableBody>
        </Table>
      </StyledTableContainer>
      <TableFilterDropdown
        anchorEl={filterAnchorEl}
        open={Boolean(filterAnchorEl)}
        onClose={handleCloseFilterDropdown}
        columnId={currentFilterColumn}
        values={Array.from(new Set(dataRows.map((row) => row[currentFilterColumn!])))}
        selectedValues={filters[currentFilterColumn!] || []}
        onFilterChange={handleFilterChange}
      />
      <ConfirmationDialog open={openDialog} onClose={handleCloseDialog} onConfirm={handleConfirmDelete} />
      <ErrorPopup
        open={errorPopupOpen}
        message={error || 'An unknown error occurred'}
        onClose={handleCloseErrorPopup}
      />
    </>
  );
};
